export default {
  common: {
    loading: "Loading…",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    confirm: "Confirm",
    delete: "Delete",
    close: "Close",
    copy: "Copy",
    dash: "—",
    genericError: "An error occurred",
    confirmPasswordPlaceholder: "Confirm password",
    passwordMismatch: "Passwords do not match",
  },
  plan: {
    FREE: "Free",
    PAID: "Paid",
  },
  nav: {
    console: "Console",
    home: "Home",
    apiKeys: "API Keys",
    subscription: "Subscription",
    myAccount: "My account",
    logout: "Sign out",
    openMenu: "Open menu",
  },
  auth: {
    tagline: "Accounts & API keys space",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Password",
    passwordMinPlaceholder: "Password (min. 8 characters)",
    loginButton: "Sign in",
    loggingIn: "Signing in…",
    noAccount: "No account yet?",
    signUp: "Sign up",
    createAccount: "Create an account",
    creating: "Creating…",
    alreadyAccount: "Already have an account?",
    signIn: "Sign in",
    forgotPasswordLink: "Forgot your password?",
  },
  passwordReset: {
    forgotTitle: "Forgot password",
    forgotDescription: "Enter your email and we'll send you a link to reset your password.",
    emailSentTitle: "Email sent",
    emailSentDescription:
      "If an account exists for this email, you'll receive a reset link within a few minutes.",
    submit: "Send reset link",
    submitting: "Sending…",
    backToLogin: "Back to sign in",
    resetTitle: "Reset your password",
    resetDescription: "Choose a new password for your account.",
    newPasswordPlaceholder: "New password (min. 8 characters)",
    resetSubmit: "Reset password",
    resetSubmitting: "Resetting…",
    resetSuccessTitle: "Password reset",
    resetSuccessDescription: "You can now sign in with your new password.",
    invalidTokenTitle: "Invalid link",
    invalidTokenDescription: "This reset link is invalid or has expired.",
    requestNewLink: "Request a new link",
  },
  account: {
    title: "My account",
    subtitle: "Manage your profile, password and account",
    profile: {
      title: "Profile",
      emailLabel: "Email",
      emailDescription: "Used to sign in to the console",
      planLabel: "Plan",
      planDescription: "The plan associated with your account",
    },
    security: {
      title: "Security",
      currentPasswordPlaceholder: "Current password",
      newPasswordPlaceholder: "New password (min. 8 characters)",
      submit: "Change password",
      updating: "Updating…",
    },
    danger: {
      title: "Danger zone",
      deleteLabel: "Delete account",
      deleteDescription: "This action is irreversible and immediately revokes all your API keys",
      passwordPlaceholder: "Password",
      deleteButton: "Delete",
      confirmTitle: "Permanently delete the account?",
      confirmDescription: "All your API keys will be revoked and this action cannot be undone.",
    },
    toast: {
      emailUpdated: "Email updated",
      passwordUpdated: "Password updated",
    },
  },
  apiKeys: {
    title: "API Keys",
    subtitle: "Manage the keys used by your self-hosted instances to validate their license.",
    newKey: "New key",
    empty: "No API keys yet.",
    table: {
      key: "Key",
      name: "Name",
      created: "Created on",
      lastUsed: "Last used",
      status: "Status",
    },
    unnamed: "Unnamed key",
    revoked: "Revoked",
    active: "Active",
    revokeAria: "Revoke",
    confirmRevokeTitle: "Revoke this API key?",
    confirmRevokeDescription:
      "Any self-hosted instance still using this key will immediately fail its license check. This cannot be undone.",
    dialog: {
      createdTitle: "API key created",
      createdDescription: "Copy this key now — it will never be shown again.",
      copyAria: "Copy",
      createTitle: "Create an API key",
      createDescription: "This key will let a self-hosted instance validate its license.",
      namePlaceholder: "Name (e.g. Production server)",
      generate: "Generate key",
      generating: "Creating…",
    },
    toast: {
      revoked: "Key revoked",
    },
  },
  billing: {
    subtitle: "Manage your plan, subscription and invoices",
    yourPlan: "Your plan",
    planDescription: {
      FREE: "Basic features, standard limits",
      PAID: "Full access to all features",
    },
    yourSubscription: "Your subscription",
    subscriptionLabel: "Subscription",
    noActiveSubscription: "No active subscription",
    nextDueDate: "{{label}} · next due date {{date}}",
    manage: "Manage subscription",
    redirecting: "Redirecting…",
    selfHostedPremium: "Self-hosted Premium",
    cloud: "Cloud",
    resync: "Check for a payment",
    resyncing: "Checking…",
    resyncFound: "Payment found, your subscription is now active.",
    resyncNotFound: "No payment found yet.",
    status: {
      active: "Active",
      trialing: "Trial period",
      past_due: "Payment overdue",
      canceled: "Canceled",
      unpaid: "Unpaid",
      incomplete: "Incomplete",
      incomplete_expired: "Expired",
    },
    invoices: {
      title: "Invoices",
      empty: "No invoices yet",
      download: "Download",
      status: {
        paid: "Paid",
        open: "Pending",
        void: "Voided",
        uncollectible: "Uncollectible",
        draft: "Draft",
      },
    },
  },
  checkout: {
    cancel: {
      title: "Payment canceled",
      description: "No amount was charged. You can try again anytime.",
      backToAccount: "Back to my account",
    },
    success: {
      notFoundTitle: "Session not found",
      notFoundDescription: "We were unable to verify this payment.",
      verifyingTitle: "Verifying payment…",
      verifyingDescription: "One moment, please wait.",
      confirmedTitle: "Payment confirmed",
      activating: "Activating your subscription…",
      timeoutDescription:
        "This is taking longer than expected. Your payment was received — activation should complete shortly. You can also refresh this page or reach out to support.",
      refresh: "Refresh",
      activatedTitle: "Subscription activated",
      activatedSelfHosted: "Thank you! Create an API key to activate your self-hosted instance.",
      activatedCloud: "Thank you! Your paid plan is now active.",
      createApiKey: "Create an API key",
      backToAccount: "Back to my account",
    },
  },
  home: {
    title: "Home",
    welcome: "Welcome, {{email}}",
    planLabel: "Plan",
    activeApiKeys: "Active API keys",
    manageApiKeys: "Manage my API keys",
    gettingStarted: {
      title: "Get started",
      description:
        "Create an API key to connect your first self-hosted instance to this license server.",
      action: "Create my first API key",
    },
  },
  error: {
    boundary: "An unexpected error occurred.",
    backHome: "Back to home",
  },
  errors: {
    STRIPE_ERROR:
      "Something went wrong with the payment provider. Please try again or contact support.",
    NO_STRIPE_CUSTOMER: "No subscription to manage yet — upgrade first.",
    CHECKOUT_SESSION_NOT_FOUND: "This checkout session could not be found.",
    API_KEY_NOT_FOUND: "This API key could not be found.",
    INVALID_RESET_TOKEN: "This password reset link is invalid or has expired.",
    ACCOUNT_ALREADY_EXISTS: "An account already exists for this email address.",
    INVALID_CREDENTIALS: "Invalid email or password.",
  },
  settings: {
    appearance: {
      sectionTitle: "Appearance",
    },
    language: {
      label: "Interface language",
      description: "Choose the language used across the console",
    },
    theme: {
      label: "Theme",
      description: "Choose the console's color scheme",
      options: {
        light: "Light",
        dark: "Dark",
        system: "System",
      },
    },
  },
} as const;
